import { MergeOrUpdateStateAction } from '@tableau/vizportalclient-common-components';
import React from 'react';

import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { CollectionsErrorCode, CommonErrorCode, DatasourceErrorCode, FlowErrorCode, MetricErrorCode, PredictiveModelErrorCode, ProjectErrorCode, SchedulingErrorCode, UserErrorCode, ViewErrorCode, WorkbookErrorCode } from '~commonInfra/utils/ErrorCodes';

import { Messages } from '../../codegen/Localize';
import { CollectionsResourceQuery } from '../../Collections/ResourceQuery/CollectionsResourceQuery';
import { COLLECTION_NAME_MAX_LENGTH } from '../../Collections/space/CollectionHeaderEditableName';
import { UpdateCollectionsResponseData } from '../../Collections/space/CollectionModels';
import { ActionDialogBodyWithTextInput, ActionDialogBodyWithTextInputProps, ActionDialogBodyWithTextInputState } from '../../commonInfra/dialogs/ActionDialogBodyWithTextInput';
import { openReactActionDialog } from '../../commonInfra/dialogs/ReactActionDialog';
import { TabDialogWidth } from '../../commonInfra/dialogs/style/TabDialogWidth';
import { L10n } from '../../commonInfra/L10n/L10n';
import { DataSources } from '../../datasource/resources/Datasources';
import { renameDraft } from '../../drafts/requests/DraftsRequests';
import { ContentDraftStatus } from '../../drafts/serverApi/types/ContentDraft';
import { isUnpublishedWorkbookDraft } from '../../drafts/utils/DraftUtils';
import { Flows } from '../../flow/resources/Flows';
import { IError } from '../../httpClient/serverApi/types/IError';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { Metrics } from '../../metrics/resources/Metrics';
import { PredictiveModels } from '../../predictiveModel/resources/PredictiveModels';
import { Projects } from '../../projects/resources/Projects';
import { PublishedConnections } from '../../publishedConnection/resources/PublishedConnections';
import { SchedulesService } from '../../schedule/resources/Schedules';
import { ExplorableItemType } from '../../spacesPlatform/models/ContentType';
import { IContentInfo } from '../../spacesPlatform/models/IContentInfo';
import { INamedItemInfo } from '../../spacesPlatform/models/resourceInfoParts';
import { Groups } from '../../userManagement/groups/Groups';
import { Views } from '../../views/resources/Views';
import { Workbooks } from '../../workbook/resources/Workbooks';

interface RenameActionParams {
  item: INamedItemInfo;
  dialogTitle: string;
  dialogDescription: string;
  renameFn: (id: string, name: string) => Promise<IResult>;
  getSuccessMessage: (newName: string) => string;
  defaultErrorMessage: string;
  maxNameLength?: number;
  errorMessageOnOpen?: string;
  isOwner?: boolean;
}

class RenameActionClass {

  public renamePublishedConnection(publishedConnection: INamedItemInfo): Promise<IResult> {
    return rename({
      item: publishedConnection,
      dialogTitle: Messages.renamePublishedConnection(),
      dialogDescription: Messages.confirmRenamePublishedConnection({ PUBLISHED_CONNECTION_NAME: L10n.elideIfLong(publishedConnection.name) }),
      renameFn: (id: string, name: string) => PublishedConnections.rename(id, name),
      getSuccessMessage: (newName: string) => Messages.publishedConnectionRenamed({ PUBLISHED_CONNECTION_NAME: L10n.elideIfLong(newName) }),
      defaultErrorMessage: Messages.publishedConnectionRenameError({ PUBLISHED_CONNECTION_NAME: L10n.elideIfLong(publishedConnection.name) })
    });
  }

  public renamePredictiveModel(predictiveModel: INamedItemInfo): Promise<IResult> {
    return rename({
      item: predictiveModel,
      dialogTitle: Messages.renamePredictiveModel(),
      dialogDescription: Messages.confirmRenamePredictiveModel({ PREDICTIVE_MODEL_NAME: L10n.elideIfLong(predictiveModel.name) }),
      renameFn: (id: string, name: string) => PredictiveModels.rename(id, name),
      getSuccessMessage: (newName: string) => Messages.predictiveModelRenamed({ PREDICTIVE_MODEL_NAME: L10n.elideIfLong(newName) }),
      defaultErrorMessage: Messages.predictiveModelRenameError({ PREDICTIVE_MODEL_NAME: L10n.elideIfLong(predictiveModel.name) })
    });
  }

  public renameProject(project: INamedItemInfo): Promise<IResult> {
    return rename({
      item: project,
      dialogTitle: Messages.renameProject(),
      dialogDescription: Messages.confirmRenameProject({ PROJECT_NAME: L10n.elideIfLong(project.name) }),
      renameFn: (id: string, name: string) => Projects.rename(id, name),
      getSuccessMessage: (newName: string) => Messages.projectRenamed({ PROJECT_NAME: L10n.elideIfLong(newName) }),
      defaultErrorMessage: Messages.projectRenameError({ PROJECT_NAME: L10n.elideIfLong(project.name) })
    });
  }

  public renameWorkbook(workbook: IContentInfo<'workbook'>): Promise<IResult> {
    const isUnpublishedDraft = isUnpublishedWorkbookDraft(workbook);

    return rename({
      item: {
        ...workbook,
        id: isUnpublishedDraft ? workbook.draftLuid : workbook.id,
      },
      dialogTitle: Messages.renameWorkbook(),
      dialogDescription: Messages.confirmRenameWorkbook(),
      renameFn: (id: string, name: string) => isUnpublishedDraft ? renameDraft(id, name) : Workbooks.rename(id, name),
      getSuccessMessage: (newName: string) => Messages.workbookRenamed({ WORKBOOK_NAME: L10n.elideIfLong(newName) }),
      defaultErrorMessage: Messages.workbookRenameError({ WORKBOOK_NAME: L10n.elideIfLong(workbook.name) })
    });
  }

  public renameView(view: INamedItemInfo): Promise<IResult> {
    return rename({
      item: view,
      dialogTitle: Messages.renameView(),
      dialogDescription: Messages.confirmRenameView(),
      renameFn: (id: string, name: string) => Views.rename(id, name),
      getSuccessMessage: (newName: string) => Messages.viewRenamed({ VIEW_NAME: L10n.elideIfLong(newName) }),
      defaultErrorMessage: Messages.viewRenameError({ VIEW_NAME: L10n.elideIfLong(view.name) })
    });
  }

  public renameSchedule(schedule: INamedItemInfo): Promise<IResult> {
    return rename({
      item: schedule,
      dialogTitle: Messages.renameSchedule(),
      dialogDescription: Messages.confirmRenameSchedule({ SCHEDULE_NAME: L10n.elideIfLong(schedule.name) }),
      renameFn: (id: string, name: string) => SchedulesService.rename(id, name),
      getSuccessMessage: (newName: string) => Messages.updateScheduleNameSuccess({ ITEM_NAME: schedule.name, NEW_ITEM_NAME: L10n.elideIfLong(newName) }),
      defaultErrorMessage: Messages.scheduleRenameError({ SCHEDULE_NAME: L10n.elideIfLong(schedule.name) })
    });
  }

  public renameFlow(flow: IContentInfo<'flow'>): Promise<IResult> {
    const isNeverPublishedFlow = flow.draftStatus === ContentDraftStatus.unpublished;
    return rename({
      item: flow,
      dialogTitle: Messages.renameFlow(),
      dialogDescription: Messages.confirmRenameFlow({ FLOW_NAME: L10n.elideIfLong(flow.name) }),
      renameFn: (id: string, name: string) => isNeverPublishedFlow ? Flows.renameDraft(id, name) : Flows.rename(id, name),
      getSuccessMessage: (newName: string) => Messages.flowRenamed({ FLOW_NAME: L10n.elideIfLong(newName) }),
      defaultErrorMessage: Messages.flowRenameError({ FLOW_NAME: L10n.elideIfLong(flow.name) })
    });
  }

  public renameMetric(metric: INamedItemInfo): Promise<IResult> {
    return rename({
      item: metric,
      dialogTitle: Messages.renameMetric(),
      dialogDescription: Messages.confirmRenameMetric({ METRIC_NAME: L10n.elideIfLong(metric.name) }),
      renameFn: (id: string, name: string) => Metrics.rename(id, name),
      getSuccessMessage: (newName: string) => Messages.metricRenamed({ METRIC_NAME: L10n.elideIfLong(newName) }),
      defaultErrorMessage: Messages.metricRenameError({ METRIC_NAME: L10n.elideIfLong(metric.name) })
    });
  }

  public renameCollection(collection: IContentInfo<ExplorableItemType>, isOwner: boolean): Promise<IResult> {
    return rename({
      item: collection,
      dialogTitle: Messages.renameCollection(),
      dialogDescription: Messages.confirmRenameCollection({ COLLECTION_NAME: L10n.elideIfLong(collection.name) }),
      renameFn: (id: string, name: string) => {
        return (new CollectionsResourceQuery).update([{luid: collection.luid, name: name}])
        .then((response: UpdateCollectionsResponseData) => {
          if (response.errors && response.errors.length > 0) {
            return {errors: [{code: response.errors[0].errorCode, id: collection.id}]} as IResult;
          } else {
            return {};
          }
        })
        .catch((error: any) => {
          return {errors: [{code: error.data.errorCode, id: collection.id}]} as IResult;
        });
      },
      getSuccessMessage: (newName: string) => Messages.collectionRenamed({ COLLECTION_NAME: L10n.elideIfLong(newName) }),
      defaultErrorMessage: Messages.collectionRenameError({ COLLECTION_NAME: L10n.elideIfLong(collection.name) }),
      maxNameLength: COLLECTION_NAME_MAX_LENGTH,
      errorMessageOnOpen: collection.name.length > COLLECTION_NAME_MAX_LENGTH && Messages.createCollectionNameTooLongError(),
      isOwner,
    });
  }

  public renameGroup(group: INamedItemInfo): Promise<IResult> {
    return rename({
      item: group,
      dialogTitle: Messages.renameGroup(),
      dialogDescription: Messages.updateGroupNameDesc({ GROUP_NAME: L10n.elideIfLong(group.name) }),
      renameFn: (id: string, name: string) => Groups.updateGroupName(id, name),
      getSuccessMessage: (newName: string) => Messages.updateGroupNameSuccessMsg({ ITEM_NAME: group.name, NEW_ITEM_NAME: L10n.elideIfLong(newName) }),
      defaultErrorMessage: Messages.groupRenameError({ GROUP_NAME: L10n.elideIfLong(group.name) })
    });
  }

  public renameDatasource(datasource: INamedItemInfo): Promise<IResult> {
    return rename({
      item: datasource,
      dialogTitle: Messages.renameDatasource(),
      dialogDescription: Messages.confirmRenameDatasource(),
      renameFn: (id: string, name: string) => DataSources.rename(id, name),
      getSuccessMessage: (newName: string) => Messages.datasourceRenamed({ DATASOURCE_NAME: L10n.elideIfLong(newName) }),
      defaultErrorMessage: Messages.datasourceRenameError({ DATASOURCE_NAME: L10n.elideIfLong(datasource.name) })
    });
  }
}

function rename(params: RenameActionParams): Promise<IResult> {
  const {
    item, dialogTitle, dialogDescription, renameFn, getSuccessMessage, defaultErrorMessage, maxNameLength, errorMessageOnOpen, isOwner
  } = params;

  return openReactActionDialog<ActionDialogBodyWithTextInputProps, ActionDialogBodyWithTextInputState, IResult>({
    title: dialogTitle,
    bodyComponent: ActionDialogBodyWithTextInput,
    initialDialogState: {
      text: item.name,
      errorMessage: errorMessageOnOpen,
    },
    initialBodyProps: {
      originalText: item.name,
      label: dialogDescription,
      maxLength: maxNameLength || 255,
    },
    actionName: Messages.rename(),
    actionDisabled: (props, state) => state.text.trim().length === 0 || state.text.length > props.maxLength || state.text === props.originalText,
    action: (_, state, updateDialogState) => executeRename({
      item,
      renameFn,
      defaultErrorMessage,
      getSuccessMessage,
      isOwner,
      newName: state.text,
      updateDialogState
    }),
    dialogWidth: TabDialogWidth.width375px,
    dismissDialogOnActionFailed: false,
  }).result;
}

type ExecuteRenameParams =
  Pick<RenameActionParams, 'item' | 'renameFn' | 'defaultErrorMessage' | 'getSuccessMessage' | 'isOwner'> &
  {
    updateDialogState: React.Dispatch<MergeOrUpdateStateAction<ActionDialogBodyWithTextInputState>>;
    newName: string;
  };

async function executeRename(
  { item, renameFn, defaultErrorMessage, getSuccessMessage, isOwner, newName, updateDialogState }: ExecuteRenameParams
) {
  const result: IResult = await renameFn(item.id, newName);
  if (result.errors && result.errors.length > 0) {
    const errorMessage = getErrorMessage(result.errors[0], defaultErrorMessage, item.name, newName, isOwner);
    updateDialogState({ errorMessage });
    return Promise.reject();
  } else {
    const successMessage = getSuccessMessage(newName);
    ToasterService.instance.pop({ type: ToastType.Success, text: successMessage });
  }
}

function getErrorMessage(error: IError, defaultErrorMessage: string, oldName?: string, newName?: string, isOwner?: boolean) {
  switch (error.code) {
    case FlowErrorCode.FlowNameAlreadyExists:
      return Messages.errorFlowNameAlreadyExists();
    case MetricErrorCode.MetricNameAlreadyExists:
      return Messages.errorMetricNameAlreadyExists();
    case ProjectErrorCode.ProjectNameAlreadyExists:
      return Messages.errorProjectNameAlreadyExists();
    case SchedulingErrorCode.SchedulingNameAlreadyExists:
      return Messages.errorScheduleNameAlreadyExists();
    case ViewErrorCode.ViewNameAlreadyExists:
      return Messages.errorViewNameAlreadyExists();
    case WorkbookErrorCode.WorkbookNameAlreadyExists:
      return Messages.errorWorkbookNameAlreadyExists();
    case CollectionsErrorCode.DUPLICATE_KEY:
      return isOwner ? Messages.collectionOwnerDuplicateRenameError(): Messages.collectionNonOwnerDuplicateRenameError();
    case UserErrorCode.UserGroupInvalidName:
      return Messages.errorUserGroupInvalidName();
    case UserErrorCode.UserGroupWithNameAlreadyExists:
      return Messages.errorUserGroupWithNameAlreadyExists();
    case UserErrorCode.UserAllUsersGroupAlreadyExists:
      return Messages.errorUserAllUsersGroupAlreadyExists({ NEW_ITEM_NAME: L10n.elideIfLong(newName) });
    case CommonErrorCode.GroupNotFound:
      return Messages.errorGroupNotFound({ ITEM_NAME: L10n.elideIfLong(oldName) });
    case DatasourceErrorCode.DatasourceWithSameNameInDestinationProject:
    case DatasourceErrorCode.DatasourceNameAlreadyExists:
      return Messages.errorDatasourceWithSameNameInDestinationProject();
    case PredictiveModelErrorCode.PredictiveModelWithSameNameInDestinationContainer:
      return Messages.errorPredictiveModelNameAlreadyExistsInDestinationProject();
    default:
      return defaultErrorMessage;
  }
}


export const RenameAction = new RenameActionClass();
